import type {
  AmplifyEvent,
  AmplifyModuleClickEvent,
  AmplifyProductAddToFavoritesEvent,
  AmplifyProductClickEvent,
  AmplifyProductFilterEvent,
  AmplitudeEventBase,
  ReturnStepEventData
} from 'types/amplitude';

export const trackAmplitudeEvent = <EventData extends AmplifyEvent>(data: EventData) =>
  new Promise(resolve => {
    if (typeof window !== 'undefined' && Array.isArray(window.dataLayer)) {
      const callback = () => {
        window.dataLayer.push(data);
        resolve(null);
      };
      if (window.requestIdleCallback) {
        window.requestIdleCallback(callback);
        return;
      } else {
        setTimeout(callback, 5);
        return;
      }
    }
    resolve(null);
  });

/**
 * Fires a generic Amplitude event with full type safety.
 * This is the universal function for tracking any Amplitude event that extends AmplitudeEventBase.
 * Use this for new pattern events that include component tracking.
 *
 * @template T - The specific Amplitude event type that extends AmplitudeEventBase
 * @param data - The complete event data including event type, component info, and any additional properties
 * @returns Promise that resolves when the event is pushed to the dataLayer
 *
 * @example
 * ```typescript
 * // Click event
 * fireAmplitudeEvent<AmplitudeRecoClickEvent>({
 *   event: 'click',
 *   component: {
 *     name: 'Recommendation',
 *     type: 'product',
 *     context: 'Discover Our Bestsellers'
 *     pageType: 'plp'
 *   },
 *   product: { sku: '123', ... },
 *   sessionId: 'session-123'
 * });
 *
 * // Can be used for any event type (click, impression, hover, etc.)
 * fireAmplitudeEvent<SomeOtherEvent>({
 *   event: 'impression',
 *   component: { name: 'Banner', type: 'promo', context: 'Homepage Hero', pageType: 'home' },
 *   sessionId: 'session-123'
 * });
 * ```
 */
export const fireAmplitudeEvent = <T extends AmplitudeEventBase>(data: T) => trackAmplitudeEvent<T>(data);

export const fireAmplitudeProductClickedEvent = (data: AmplifyProductClickEvent) => trackAmplitudeEvent<AmplifyProductClickEvent>(data);

export const fireAmplitudeModuleClickedEvent = (data: AmplifyModuleClickEvent) => trackAmplitudeEvent<AmplifyModuleClickEvent>(data);

export const fireAmplitudeProductAddToFavoritesEvent = (data: AmplifyProductAddToFavoritesEvent) =>
  trackAmplitudeEvent<AmplifyProductAddToFavoritesEvent>(data);

export const fireReturnStepAmplitudeEvent = (eventData: ReturnStepEventData) => {
  trackAmplitudeEvent<ReturnStepEventData>(eventData);
};
export const fireAmplitudeProductFilterEvent = (data: AmplifyProductFilterEvent) => trackAmplitudeEvent<AmplifyProductFilterEvent>(data);
